import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  Block,
  BlockUpdateFields,
  DataOperation,
} from '@worldathletics/types';
import {
  addNodeRecursive,
  collectDescendantIds,
  isDescendant,
  normalizeDocumentData,
  reindexChildren,
} from '../helpers';
import {
  ackPendingEntries,
  ackPendingSequences,
  type PendingOperationEntry,
} from '@/lib/pending-data-operations';

// Normalised entity stored in `byId`. For containers we drop `blocks`
// (children live in `state.childrenById`); other variants of the
// discriminated union don't carry `blocks` to begin with.
// Distributive Omit so the discriminated union survives the field removal.
// `Omit<Block, 'blocks'>` collapses the union into a single object type;
// `T extends unknown ? Omit<T, 'blocks'> : never` distributes Omit per
// variant, preserving narrowing (e.g. `DocumentEntity & { type: 'container' }`
// then resolves to the container variant's fields).
type DistributiveOmit<T, K extends keyof never> = T extends unknown
  ? Omit<T, K>
  : never;

export type DocumentEntity = DistributiveOmit<Block, 'blocks'>;

export type SetDocumentDataPayload = {
  blocks: Block[];
  nodeKey?: string | null;
  /** Last-known server block ids for missing-add prep. Defaults to all ids in `blocks`. */
  serverBlockIds?: string[];
};

export type DocumentsState = {
  byId: Record<string, DocumentEntity>;
  rootIds: string[];
  childrenById: Record<string, string[]>;
  parentById: Record<string, string | null>;
  isLoaded: boolean;
  isDirty: boolean;
  // `${bookId}:${nodeId}` for the node whose blocks are in this slice.
  sourceNodeKey: string | null;
  // Sequenced log of operations since the last hydrate / ack. Save snapshots
  // throughSequence, then acknowledges only entries at or below that value so
  // edits appended while a request is in flight remain queued.
  pendingOps: PendingOperationEntry[];
  nextPendingSequence: number;
  // Revision counters for selector gating (typing must not walk the full tree).
  structureRevision: number;
  namingRevision: number;
  contentRevision: number;
  // Last-acknowledged server-side block ids for the current node. Used by
  // missing-add preparation instead of a potentially stale `documentNode.data`
  // closure.
  serverBlockIds: string[];
};

type AddNodePayload = {
  node: Block;
  parentId?: string | null;
  index?: number;
};

type UpdateNodePayload = {
  id: string;
  changes: Partial<DocumentEntity>;
};

type SetNodeFieldsPayload = {
  id: string;
  fields: Partial<DocumentEntity>;
};

type RemoveNodePayload = {
  id: string;
};

type MoveNodePayload = {
  id: string;
  newParentId?: string | null;
  newIndex?: number;
};

type ReorderChildrenPayload = {
  parentId?: string | null;
  childIds: string[];
};

// `DocumentEntity` is a discriminated union (one variant per block type), so
// `keyof DocumentEntity` only yields the fields common to every variant
// ('id' | 'type' | 'order'). These sets need to recognize field names from
// any single variant (e.g. 'text' only exists on the text variant), so they
// key on `string` rather than `keyof DocumentEntity`.
const NamingFieldKeys = new Set<string>([
  'title',
  'prefix',
  'numberingStyle',
  'propagateNumbering',
  'continueNumbering',
  'numberingStylePosition',
  'style',
  'transparent',
]);

const ContentFieldKeys = new Set<string>([
  'text',
  'items',
  'rows',
  'columns',
  'caption',
  'src',
  'alt',
  'width',
  'height',
  'alignment',
  'headerRowCount',
  'rowHeaderCol',
  'meta',
]);

export function collectAllBlockIds(blocks: Block[]): string[] {
  const ids: string[] = [];
  const visit = (nodes: Block[]) => {
    for (const node of nodes) {
      ids.push(node.id);
      if (node.type === 'container' && node.blocks?.length) {
        visit(node.blocks);
      }
    }
  };
  visit(blocks);
  return ids;
}

function pushPendingOp(state: DocumentsState, operation: DataOperation): void {
  state.nextPendingSequence += 1;
  state.pendingOps.push({
    sequence: state.nextPendingSequence,
    operation,
  });
  state.isDirty = true;
}

function bumpRevisionsForFields(
  state: DocumentsState,
  fields: Partial<DocumentEntity>,
): void {
  let naming = false;
  let content = false;
  for (const key of Object.keys(fields)) {
    if (NamingFieldKeys.has(key)) naming = true;
    if (ContentFieldKeys.has(key)) content = true;
  }
  if (naming) state.namingRevision += 1;
  if (content) state.contentRevision += 1;
}

export const initialDocumentsState: DocumentsState = {
  byId: {},
  rootIds: [],
  childrenById: {},
  parentById: {},
  isLoaded: false,
  isDirty: false,
  sourceNodeKey: null,
  pendingOps: [],
  nextPendingSequence: 0,
  structureRevision: 0,
  namingRevision: 0,
  contentRevision: 0,
  serverBlockIds: [],
};

export const documentDataSlice = createSlice({
  name: 'documentData',
  initialState: initialDocumentsState,
  reducers: {
    setIsLoadedData: (state, action: PayloadAction<boolean>) => {
      state.isLoaded = action.payload;
    },

    setDocumentData: (
      state,
      action: PayloadAction<Block[] | SetDocumentDataPayload>,
    ) => {
      const blocks = Array.isArray(action.payload)
        ? action.payload
        : action.payload.blocks;
      const nodeKey = Array.isArray(action.payload)
        ? null
        : (action.payload.nodeKey ?? null);
      const serverBlockIds = Array.isArray(action.payload)
        ? collectAllBlockIds(blocks)
        : (action.payload.serverBlockIds ?? collectAllBlockIds(blocks));
      const next = normalizeDocumentData(blocks);
      next.sourceNodeKey = nodeKey;
      next.isDirty = false;
      next.serverBlockIds = serverBlockIds;
      // Keep the pending-op sequence monotonic across re-hydration. Resetting
      // it to 0 lets an in-flight save that snapshotted `throughSequence = N`
      // ack post-hydration entries numbered 1..N when it resolves — those ops
      // are dropped, `isDirty` recomputes to false, and the edit is silently
      // lost. Sequences are client-only (never sent to the API), so making the
      // counter monotonic for the store's lifetime costs nothing.
      next.nextPendingSequence = state.nextPendingSequence;
      return next;
    },

    setIsDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },

    clearPendingOps: state => {
      state.pendingOps = [];
      state.isDirty = false;
    },

    ackPendingOperations: (
      state,
      action: PayloadAction<{ throughSequence: number }>,
    ) => {
      state.pendingOps = ackPendingEntries(
        state.pendingOps,
        action.payload.throughSequence,
      );
      state.isDirty = state.pendingOps.length > 0;
    },

    // Acks exactly the given sequences rather than every entry at or below a
    // threshold — for partial-window saves (e.g. persisting a single source
    // block's text) where entries outside the sent window can carry lower
    // sequence numbers than entries inside it.
    ackPendingOperationsBySequence: (
      state,
      action: PayloadAction<{ sequences: number[] }>,
    ) => {
      state.pendingOps = ackPendingSequences(
        state.pendingOps,
        action.payload.sequences,
      );
      state.isDirty = state.pendingOps.length > 0;
    },

    setServerBlockIds: (state, action: PayloadAction<string[]>) => {
      state.serverBlockIds = action.payload;
    },

    clearDocumentData: () => initialDocumentsState,

    addNode: (state, action: PayloadAction<AddNodePayload>) => {
      const { node, parentId = null, index } = action.payload;

      if (state.byId[node.id]) {
        throw new Error(`Node already exists`);
      }

      addNodeRecursive(state, node, parentId);

      const insertedIndex =
        parentId === null
          ? (index ?? state.rootIds.length)
          : (index ?? (state.childrenById[parentId]?.length ?? 0));
      if (parentId === null) {
        state.rootIds.splice(insertedIndex, 0, node.id);
        reindexChildren(state, null);
      } else {
        const children = state.childrenById[parentId] ?? [];
        children.splice(insertedIndex, 0, node.id);
        state.childrenById[parentId] = children;
        reindexChildren(state, parentId);
      }

      state.structureRevision += 1;
      state.namingRevision += 1;
      // Send the FULL block (with potential nested children) — the BE's
      // createDataNodes recursion handles the subtree in one op.
      pushPendingOp(state, {
        op: 'add',
        parentId,
        index: insertedIndex,
        block: node,
      });
      // The BE `add` op writes the new row at `block.order` but does NOT
      // shift existing siblings. Without a trailing `reorder`, the new
      // block's `order` collides with an existing sibling (e.g., inserting
      // at index 0 puts both the new block and the original first sibling
      // at order=0). Postgres ties on equal `order` are non-deterministic
      // → user perceives the new block as "disappeared after save". The
      // `reorder` op renumbers all siblings of the affected parent to
      // match Redux's resolved 0..N-1 order.
      const destChildren =
        parentId === null
          ? state.rootIds
          : (state.childrenById[parentId] ?? []);
      pushPendingOp(state, {
        op: 'reorder',
        parentId,
        childIds: [...destChildren],
      });
    },

    updateNode: (state, action: PayloadAction<UpdateNodePayload>) => {
      const { id, changes } = action.payload;
      const current = state.byId[id];
      if (!current) return;

      const hasChange = (
        Object.entries(changes) as [keyof DocumentEntity, unknown][]
      ).some(([key, value]) => current[key as keyof typeof current] !== value);
      if (!hasChange) return;

      // Spread on a discriminated union widens `type` to the union of literals
      // and TS can no longer match the result against any single variant, so
      // we assign back via the `byId` map type. `changes` is `Partial<DocumentEntity>`
      // — callers supply variant-compatible fields; the redux slice doesn't
      // re-validate the discriminator here.
      state.byId[id] = {
        ...current,
        ...changes,
      } as DocumentEntity;

      bumpRevisionsForFields(state, changes);

      const fields = pickUpdatableFields(changes);
      if (Object.keys(fields).length > 0) {
        pushPendingOp(state, { op: 'update', id, fields });
      } else {
        // Structural-only local fields still mark dirty if they mutated entity.
        state.isDirty = true;
      }
    },

    // Local-only field sync after a successful server write — does not enqueue
    // pendingOps or mark the document dirty (used after reference token persist).
    setNodeFields: (state, action: PayloadAction<SetNodeFieldsPayload>) => {
      const { id, fields } = action.payload;
      const current = state.byId[id];
      if (!current) return;
      state.byId[id] = { ...current, ...fields } as DocumentEntity;
      bumpRevisionsForFields(state, fields);
    },

    removeNode: (state, action: PayloadAction<RemoveNodePayload>) => {
      const { id } = action.payload;

      // Unknown ids (e.g. unresolved EditorJS short ids) must be a no-op —
      // otherwise we corrupt childrenById/parentById with undefined keys.
      if (!(id in state.byId)) {
        return;
      }

      const parentId = state.parentById[id];
      const idsToRemove = collectDescendantIds(state, id);

      // remove from parent
      if (parentId === null) {
        state.rootIds = state.rootIds.filter(x => x !== id);
        reindexChildren(state, null);
      } else {
        state.childrenById[parentId] = (
          state.childrenById[parentId] ?? []
        ).filter(x => x !== id);
        reindexChildren(state, parentId);
      }

      // delete all descendants
      idsToRemove.forEach((nodeId: string) => {
        delete state.byId[nodeId];
        delete state.childrenById[nodeId];
        delete state.parentById[nodeId];
      });

      state.structureRevision += 1;
      state.namingRevision += 1;
      // BE cascade deletes descendants via the parent FK, so we only need
      // one remove op for the root of the removed subtree.
      pushPendingOp(state, { op: 'remove', id });
    },

    moveNode: (state, action: PayloadAction<MoveNodePayload>) => {
      const { id, newParentId = null, newIndex } = action.payload;

      const currentParent = state.parentById[id];

      if (newParentId === id) {
        throw new Error('Cannot move node into itself');
      }

      if (newParentId && isDescendant(state, id, newParentId)) {
        throw new Error('Cannot move into descendant');
      }

      // remove from old parent
      if (currentParent === null) {
        state.rootIds = state.rootIds.filter(x => x !== id);
        reindexChildren(state, null);
      } else {
        state.childrenById[currentParent] = state.childrenById[
          currentParent
        ].filter(x => x !== id);
        reindexChildren(state, currentParent);
      }

      // add to new parent
      const finalIndex =
        newParentId === null
          ? (newIndex ?? state.rootIds.length)
          : (newIndex ?? (state.childrenById[newParentId]?.length ?? 0));
      if (newParentId === null) {
        state.rootIds.splice(finalIndex, 0, id);
        state.parentById[id] = null;
        reindexChildren(state, null);
      } else {
        const children = state.childrenById[newParentId] ?? [];
        children.splice(finalIndex, 0, id);
        state.childrenById[newParentId] = children;
        state.parentById[id] = newParentId;
        reindexChildren(state, newParentId);
      }

      state.structureRevision += 1;
      state.namingRevision += 1;
      // The BE `move` op only updates the moved row's parentId + order. Without
      // a trailing `reorder`, the OTHER siblings keep their stale `order`
      // values from before the move, causing collisions and non-deterministic
      // load-order — i.e., the user's reorder "doesn't save". Push the `move`
      // (for the parentId change) plus `reorder` op(s) for the affected parent
      // (and the source parent on cross-parent moves) so the BE renumbers all
      // siblings to match Redux's resolved 0..N-1 order.
      pushPendingOp(state, {
        op: 'move',
        id,
        newParentId,
        newIndex: finalIndex,
      });
      if (currentParent !== newParentId) {
        // Cross-parent move: source parent needs renumbering too.
        const sourceChildren =
          currentParent === null
            ? state.rootIds
            : (state.childrenById[currentParent] ?? []);
        pushPendingOp(state, {
          op: 'reorder',
          parentId: currentParent,
          childIds: [...sourceChildren],
        });
      }
      const destChildren =
        newParentId === null
          ? state.rootIds
          : (state.childrenById[newParentId] ?? []);
      pushPendingOp(state, {
        op: 'reorder',
        parentId: newParentId,
        childIds: [...destChildren],
      });
    },

    reorderChildren: (state, action: PayloadAction<ReorderChildrenPayload>) => {
      const { parentId = null, childIds } = action.payload;
      const seen = new Set<string>();
      const sanitizedChildIds = childIds.filter(id => {
        if (!state.byId[id] || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      if (parentId === null) {
        state.rootIds = sanitizedChildIds;
        reindexChildren(state, null);
      } else {
        state.childrenById[parentId] = sanitizedChildIds;
        reindexChildren(state, parentId);
      }

      state.structureRevision += 1;
      state.namingRevision += 1;
      pushPendingOp(state, {
        op: 'reorder',
        parentId,
        childIds: sanitizedChildIds,
      });
    },
  },
});

// Whitelist of fields the BE's `update` op accepts. Anything outside this set
// is a structural concern handled by add/remove/move/reorder, not update.
const UPDATABLE_FIELDS: ReadonlySet<keyof BlockUpdateFields> = new Set([
  'style',
  'prefix',
  'title',
  'text',
  'order',
  'numberingStyle',
  'propagateNumbering',
  'continueNumbering',
  'numberingStylePosition',
  'transparent',
  'numberingExempt',
  'caption',
  'headerRowCount',
  'rowHeaderCol',
  'columns',
  'rows',
  'src',
  'alt',
  'width',
  'height',
  'alignment',
  'items',
  'meta',
]);

export function pickUpdatableFields(
  changes: Partial<DocumentEntity>,
): BlockUpdateFields {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(changes)) {
    if (UPDATABLE_FIELDS.has(k as keyof BlockUpdateFields)) {
      out[k] = v;
    }
  }
  return out as BlockUpdateFields;
}

export const {
  setDocumentData,
  clearDocumentData,
  setIsLoadedData,
  setIsDirty,
  clearPendingOps,
  ackPendingOperations,
  ackPendingOperationsBySequence,
  setServerBlockIds,
  addNode,
  removeNode,
  updateNode,
  setNodeFields,
  moveNode,
  reorderChildren,
} = documentDataSlice.actions;

export default documentDataSlice.reducer;
